import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  findContactByLocatiecode,
  InkooporderError,
  isLocatiecodeFormat,
  submitInkooporder,
  type LocatieMatch,
} from '../../lib/inkooporder';

interface InkooporderWizardDialogProps {
  accountId: string;
  accountName: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (workOrderId: string, workOrderNumber: string, contactCreated: boolean, locatiecode: string) => void;
}

interface FormState {
  inkoopordernummer: string;
  locatiecode: string;
  vestigingsnaam: string;
  straatHuisnummer: string;
  postcode: string;
  plaats: string;
  managerNaam: string;
  managerEmail: string;
  managerTelefoon: string;
  aantalKleden: string;
  gewensteAfleverDatum: string;
  inkoopordertotaalText: string;
}

const EMPTY_FORM: FormState = {
  inkoopordernummer: '',
  locatiecode: '',
  vestigingsnaam: '',
  straatHuisnummer: '',
  postcode: '',
  plaats: '',
  managerNaam: '',
  managerEmail: '',
  managerTelefoon: '',
  aantalKleden: '1',
  gewensteAfleverDatum: '',
  inkoopordertotaalText: '',
};

export function InkooporderWizardDialog({
  accountId,
  accountName,
  isOpen,
  onClose,
  onCreated,
}: InkooporderWizardDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [match, setMatch] = useState<LocatieMatch | null>(null);
  const [matchSearching, setMatchSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setMatch(null);
      setError(null);
    }
  }, [isOpen]);

  // Debounced locatiecode lookup
  useEffect(() => {
    if (!isOpen) return;
    const code = form.locatiecode.trim();
    if (!isLocatiecodeFormat(code)) {
      setMatch(null);
      return;
    }
    setMatchSearching(true);
    const handle = setTimeout(async () => {
      try {
        const result = await findContactByLocatiecode(accountId, code);
        setMatch(result);
      } finally {
        setMatchSearching(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [accountId, form.locatiecode, isOpen]);

  if (!isOpen) return null;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const aantalKledenNum = parseInt(form.aantalKleden, 10);
  const aantalIsValid = Number.isInteger(aantalKledenNum) && aantalKledenNum >= 1 && aantalKledenNum <= 50;

  const requiredOk =
    form.inkoopordernummer.trim() &&
    form.locatiecode.trim() &&
    form.vestigingsnaam.trim() &&
    form.straatHuisnummer.trim() &&
    form.postcode.trim() &&
    form.plaats.trim() &&
    aantalIsValid;

  const handleSubmit = async () => {
    if (!requiredOk || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitInkooporder({
        parentAccountId: accountId,
        inkoopordernummer: form.inkoopordernummer.trim(),
        locatiecode: form.locatiecode.trim(),
        vestigingsnaam: form.vestigingsnaam.trim(),
        straatHuisnummer: form.straatHuisnummer.trim(),
        postcode: form.postcode.trim(),
        plaats: form.plaats.trim(),
        managerNaam: form.managerNaam.trim() || undefined,
        managerEmail: form.managerEmail.trim() || undefined,
        managerTelefoon: form.managerTelefoon.trim() || undefined,
        aantalKleden: aantalKledenNum,
        gewensteAfleverDatum: form.gewensteAfleverDatum || undefined,
        inkoopordertotaalText: form.inkoopordertotaalText.trim() || undefined,
      });
      onCreated(result.workOrderId, result.workOrderNumber, result.contactCreated, form.locatiecode.trim());
    } catch (err) {
      if (err instanceof InkooporderError) {
        setError(`${err.code}: ${err.message}`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Aanmaken mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Inkooporder verwerken — {accountName}</h2>
          <p className="text-xs text-slate-500 mt-1">
            Tarief: Wederverkoper · Pakket: Reiniging &amp; Protect · Afmetingen + prijs volgen bij voorinspectie
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Rij 1: inkooporder + locatiecode */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inkoopordernummer" required>
              <input
                type="text"
                value={form.inkoopordernummer}
                onChange={(e) => update('inkoopordernummer', e.target.value)}
                placeholder="I.55.26.026666"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
            <Field label="Locatiecode" required hint={matchSearching ? 'Zoeken…' : undefined}>
              <input
                type="text"
                value={form.locatiecode}
                onChange={(e) => update('locatiecode', e.target.value)}
                placeholder="370700"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
          </div>

          {/* Match-card */}
          {match && (
            <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-xs text-emerald-900">
              <strong>Locatie {match.matchedLocatiecode}</strong> al bekend bij {accountName}: {match.name}.
              Gegevens worden bijgewerkt na submit.
            </div>
          )}

          {/* Rij 2: vestigingsnaam */}
          <Field label="Vestigingsnaam" required>
            <input
              type="text"
              value={form.vestigingsnaam}
              onChange={(e) => update('vestigingsnaam', e.target.value)}
              placeholder="Koningskinderen Berenpels"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </Field>

          {/* Rij 3: adres */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Straat + huisnr" required className="col-span-2">
              <input
                type="text"
                value={form.straatHuisnummer}
                onChange={(e) => update('straatHuisnummer', e.target.value)}
                placeholder="Beerensteinerlaan 73"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
            <Field label="Postcode" required>
              <input
                type="text"
                value={form.postcode}
                onChange={(e) => update('postcode', e.target.value)}
                placeholder="1406 NR"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
          </div>
          <Field label="Plaats" required>
            <input
              type="text"
              value={form.plaats}
              onChange={(e) => update('plaats', e.target.value)}
              placeholder="Bussum"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </Field>

          {/* Rij 4: manager-trio */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Manager naam">
              <input
                type="text"
                value={form.managerNaam}
                onChange={(e) => update('managerNaam', e.target.value)}
                placeholder="Sanne Visser"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
            <Field label="Manager email">
              <input
                type="email"
                value={form.managerEmail}
                onChange={(e) => update('managerEmail', e.target.value)}
                placeholder="naam@partou.nl"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
            <Field label="Manager telefoon">
              <input
                type="tel"
                value={form.managerTelefoon}
                onChange={(e) => update('managerTelefoon', e.target.value)}
                placeholder="+31620129093"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
          </div>

          {/* Rij 5: aantal + datum + totaal */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Aantal kleden" required>
              <input
                type="number"
                min={1}
                max={50}
                value={form.aantalKleden}
                onChange={(e) => update('aantalKleden', e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
            <Field label="Gewenste afleverdatum" hint="Volgens PDF — fictief">
              <input
                type="date"
                value={form.gewensteAfleverDatum}
                onChange={(e) => update('gewensteAfleverDatum', e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
            <Field label="Inkooporder totaal (PDF)">
              <input
                type="text"
                value={form.inkoopordertotaalText}
                onChange={(e) => update('inkoopordertotaalText', e.target.value)}
                placeholder="€500,00 incl. BTW"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                disabled={submitting}
              />
            </Field>
          </div>

          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!requiredOk || submitting}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !requiredOk || submitting
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-amber-600 text-white hover:bg-amber-700',
            )}
          >
            {submitting ? 'Aanmaken…' : 'Werk Order aanmaken'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={clsx('block', className)}>
      <span className="text-sm text-slate-700">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
        {hint && <span className="text-xs text-slate-400 ml-2">{hint}</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
